<template>
  <div
    class="product-variants pb-5"
    v-if="productsInBundle?.length"
  >
    <div class="mb-4">
      <Heading
        :level="5" color="text-ash-200"
      >
        Stacks
      </Heading>
    </div>
    <ul class="space-y-3">
      <li
        v-for="variant in productsInBundle"
        :key="variant.productId"
      >
        <NuxtLink
          :id="`variant-${variant.productId}`"
          :title="`Select ${variant.productName}`"
          :href="isDisabled(variant) ? undefined : variant.productUrl"
          class="group block w-full text-left rounded-[0.4286rem] border transition
                focus:outline-none focus:ring-2 focus:ring-black"
          :class="variantClasses(variant)"
          :aria-disabled="isDisabled(variant)"
          :aria-label="`Select ${variant.productName}`"
          @click.prevent="onSelect(variant)"
          external
        >
          <div class="p-3 flex flex-col gap-1">
            <div class="text-base font-normal text-ash-500 group-hover:text-gold-100">
              {{ variant.productName }}
            </div>

            <div class="text-[0.857rem] text-gray-700">
              {{ variant.unitCount }}
            </div>

            <div
              v-if="variant.outOfStock || variant.extendedBackorder"
              class="text-xs text-red-600 mt-1"
            >
              Out of stock
            </div>
          </div>
        </NuxtLink>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import Heading from '../../../atom/heading.vue'
import type { ProductVariant } from '~~/shared/types/common'

const props = defineProps<{
  productsInBundle: ProductVariant[]
  activeProductId?: number
}>()

const emit = defineEmits<{
  (e: 'select', variant: ProductVariant): void
}>()

const activeVariantId = ref<number | string | null>(props.activeProductId ?? null)
const productsInBundle = computed(() => props.productsInBundle)
const variantClasses = computed(() => (variant: ProductVariant) => ({
  'border-ash-300 hover:border-gray-900': !isDisabled(variant),
  'border-ash-300 opacity-50 cursor-not-allowed': isDisabled(variant),
  'border-gray-900 border-[0.142857rem]': isActive(variant),
}))

const isDisabled = (variant: ProductVariant) =>
  variant.outOfStock || variant.extendedBackorder

const isActive = (variant: ProductVariant) =>
  activeVariantId.value === variant.productId

const onSelect = (variant: ProductVariant) => {
  if (isDisabled(variant)) return
  activeVariantId.value = variant.productId
  emit('select', variant)
}
</script>
